// TopoMapCoreBackend.js
// BackEnd-Dirigent (Admin) über dem geteilten topoMapCore-Kern.
//
// Gegenstück zur Frontend-Fassade (TopoMapCore): nutzt denselben Low-Level-Kern (GoogleMapsAdapter), aber für
// Admin-Seiten konfiguriert. Stellt einen kleinen KARTEN-POOL bereit: Admin-Konsumenten (z.B. das Locate-Popup)
// holen per acquireMap() eine wiederverwendbare google.maps.Map und geben sie per releaseMap() zurück, statt pro
// Aktion eine neue zu instanziieren (= Map-Load). Mehrere gleichzeitig offene Popups → mehrere Pool-Einträge, aber
// über die Session wiederverwendet (Loads = Spitzen-Parallelität statt Summe aller Öffnungen).
//
// Jede Karte lebt in einem EIGENEN Div, das zwischen einem versteckten Halter und dem jeweiligen Ziel-Container
// verschoben wird (so überlebt sie den Teardown des Popups). NICHT enthalten: Marker/Verortungs-Logik (Phase 9.2+).

import { GoogleMapsAdapter } from "./adapter/GoogleMapsAdapter.js";
import { createOverlayEditorClass } from "./editor/OverlayEditor.js";
import { createOverlayToolbarClass } from "./editor/OverlayToolbar.js";
import { ProjectionHelper } from "./tools/ProjectionHelper.js";
import { createMapTypeControlModule } from "./tools/mapTypeControl.js";

export class TopoMapCoreBackend {
    constructor(google) {
        this.google = google;
        this._pool = [];        // { adapter, mapDiv, inUse }
        this._counter = 0;
        this._holderId = 'topoBackendMapHolder';
        this._OverlayEditorClass = null;   // lazy: braucht google.maps.OverlayView (erst nach API-Load)
        this._OverlayToolbarClass = null;  // lazy: dito
    }

    /**
     * Erzeugt ein achsenparalleles Bild-Overlay zum Platzieren/Skalieren (Admin-Editor).
     * Schritt (a): read-only, API-kompatibel zu ProjectedOverlay (change/getBounds/setOpacity/remove).
     * @param {google.maps.Map} map
     * @param {{imageUrl: string, bounds: google.maps.LatLngBounds, opacity?: number,
     *          onChange?: function, editable?: boolean}} opts
     */
    createOverlayEditor(map, opts = {}) {
        if (!this._OverlayEditorClass) {
            this._OverlayEditorClass = createOverlayEditorClass(this.google);
        }
        return new this._OverlayEditorClass(map, opts.imageUrl, opts.bounds, {
            percentOpacity: opts.opacity,
            onChange: opts.onChange,
            onSelect: opts.onSelect,
            onRotateChange: opts.onRotateChange,
            onModeChange: opts.onModeChange,
            onHistoryChange: opts.onHistoryChange,   // Chef 17: History-Status → Sub-Toolbar (sonst null → kein Update)
            active: opts.active,
            aspectLock: opts.aspectLock,
            rotation: opts.rotation,
            distort: opts.distort,
            editable: opts.editable
        });
    }

    /**
     * Hängt den custom topo2-Karte/Satellit-Umschalter an eine Karte und schaltet Googles nativen mapTypeControl
     * auf DIESER Karte ab — gleiche UI wie das Frontend (Chef-Charge 3, 13), damit künftige Änderungen alle
     * Karten-Instanzen treffen. Labels über IF-Strings (MAP_TYPE_ROADMAP / MAP_TYPE_SATELLITE).
     * @param {google.maps.Map} map
     * @param {number=} position - optionale ControlPosition (Default TOP_LEFT)
     * @returns {{attach: function, destroy: function}} Handle (attach idempotent, destroy räumt auf)
     */
    createMapTypeControl(map, position) {
        return createMapTypeControlModule({ map, google: this.google, position });
    }

    /**
     * Liefert einen topoMapCore-Projektionshelfer für eine Karte — Ersatz für den Legacy-`DummyOverlay` aus basic.js.
     * Konsumenten brauchen die Projektion z.B. um Maus-/Drop-Positionen (Container-Pixel) in LatLng umzurechnen.
     * @param {google.maps.Map} map
     * @returns {ProjectionHelper}
     */
    createProjectionHelper(map) {
        return new ProjectionHelper(map);
    }

    /**
     * Erzeugt die Aktive-Overlay-Toolbar (kleines Bedien-Popup unter dem gewählten Overlay). Eine Instanz pro Karte;
     * mit attachTo(editor)/show()/hide() an die Auswahl koppeln.
     * @param {google.maps.Map} map
     * @param {{onMode?: function, onSave?: function, onReset?: function}} opts
     */
    createOverlayToolbar(map, opts = {}) {
        if (!this._OverlayToolbarClass) {
            this._OverlayToolbarClass = createOverlayToolbarClass(this.google);
        }
        return new this._OverlayToolbarClass(map, opts);
    }

    /**
     * Karten-Operationen für Konsumenten, die die rohe Map halten (z.B. overlay.js über createMap()).
     * Bündeln die `google.maps.`-Nutzung in der Fassade, damit der Aufrufer selbst google.maps.-frei bleibt.
     */
    onMapClick(map, cb) { return this.google.maps.event.addListener(map, 'click', cb); }
    onceIdle(map, cb) { return this.google.maps.event.addListenerOnce(map, 'idle', cb); }
    panTo(map, latLng) { map.panTo(latLng); }
    panToBounds(map, bounds) { map.panToBounds(bounds); }
    fitBounds(map, bounds, padding) { map.fitBounds(bounds, padding); }

    /** Geometrie-Helfer — kapseln die `google.maps.`-Konstruktoren. */
    latLng(lat, lng) { return new this.google.maps.LatLng(lat, lng); }
    latLngBounds(sw, ne) { return new this.google.maps.LatLngBounds(sw, ne); }

    /**
     * Holt eine wiederverwendbare Karte aus dem Pool (oder erzeugt eine neue) und mountet ihren Div in den Ziel-Container.
     * @param {string|HTMLElement} container - Ziel-Slot (DOM-Id oder Element)
     * @param {object} mapOptions - center/zoom/mapTypeId/… (mapId injizierbar)
     * @param {?function} onCreate - optionaler Callback `onCreate(map, adapter)`, der **nur bei Neuerzeugung** einer
     *        Pool-Karte aufgerufen wird (nicht bei Reuse). Hier richtet der Konsument **pro-Karte**-Dinge einmalig ein
     *        (z.B. Zoom-Controls, Basis-Listener) → keine Duplikate bei Wiederverwendung.
     * @returns {object|null} Pool-Handle (für releaseMap / getMapInstance)
     */
    acquireMap(container, mapOptions = {}, onCreate = null) {
        const slot = typeof container === 'string' ? document.getElementById(container) : container;
        if (!slot) {
            console.error('[TopoMapCoreBackend] acquireMap: Ziel-Container nicht gefunden:', container);
            return null;
        }

        let entry = this._pool.find(e => !e.inUse);
        if (!entry) {
            const mapDiv = document.createElement('div');
            mapDiv.id = `topoBackendMap_${++this._counter}`;
            // Karte füllt ihren Slot per absoluter Positionierung exakt aus (robuster als %-Höhe gegen
            // verschachtelte/absolut positionierte Container wie .locatemap — sonst „überragt" die Karte).
            mapDiv.style.position = 'absolute';
            mapDiv.style.top = '0';
            mapDiv.style.left = '0';
            mapDiv.style.right = '0';
            mapDiv.style.bottom = '0';
            this._getHolder().appendChild(mapDiv);

            const adapter = new GoogleMapsAdapter(this.google);
            adapter.setOverlayClass(this.google);
            adapter.initializeMap(mapDiv.id, mapOptions);

            entry = { adapter, mapDiv, inUse: false };
            this._pool.push(entry);

            // Einmalige pro-Karte-Einrichtung durch den Konsumenten (Controls, Basis-Listener) — NUR bei Neuerzeugung.
            if (typeof onCreate === 'function') {
                onCreate(adapter.getMapInstance(), adapter);
            }
        }

        entry.inUse = true;
        // Slot muss ein Positionierungs-Kontext sein, damit die absolute Karten-Füllung darauf greift.
        if (getComputedStyle(slot).position === 'static') {
            slot.style.position = 'relative';
        }
        slot.appendChild(entry.mapDiv);
        entry.adapter.triggerResize();

        // Bei Wiederverwendung die gewünschte Sicht setzen (Erst-Erzeugung hat sie via initializeMap schon).
        if (mapOptions.center != null) { entry.adapter.setCenter(mapOptions.center); }
        if (mapOptions.zoom != null) { entry.adapter.setZoom(mapOptions.zoom); }

        return entry;
    }

    /**
     * Gibt eine Karte zurück in den Pool: Div in den versteckten Halter parken (NICHT zerstören).
     * Muss aufgerufen werden, BEVOR der Container/das Popup aus dem DOM entfernt wird.
     */
    releaseMap(entry) {
        if (!entry) { return; }
        entry.inUse = false;
        if (entry.mapDiv) { this._getHolder().appendChild(entry.mapDiv); }
    }

    getMapInstance(entry) {
        return entry ? entry.adapter.getMapInstance() : null;
    }

    /** Bequemlichkeit für einfache Fälle (eine Karte, kein Release-Handling) — z.B. isolierter Test. */
    createMap(containerId, mapOptions = {}) {
        const entry = this.acquireMap(containerId, mapOptions);
        return entry ? entry.adapter.getMapInstance() : null;
    }

    _getHolder() {
        let h = document.getElementById(this._holderId);
        if (!h) {
            h = document.createElement('div');
            h.id = this._holderId;
            h.style.display = 'none';
            document.body.appendChild(h);
        }
        return h;
    }
}
